import React, { FC } from 'react';

import { CalendarIcon } from 'lucide-react';

import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger, UIButton } from '../ui';

interface DatePickerPopperProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  className?: string;
  popoverClassName?: string;
  hasActions?: boolean;
  anchorText?: string;
  children: React.ReactNode;
  placeholder: string;
  inputDataTestId?: string;
  calendarDataTestId?: string;
  hideTrigger?: boolean;
}

export const DatePickerPopper: FC<DatePickerPopperProps> = ({
  isOpen,
  onOpenChange,
  disabled,
  className,
  popoverClassName,
  anchorText,
  hasActions,
  children,
  placeholder,
  inputDataTestId,
  calendarDataTestId,
  hideTrigger = false
}) => {
  if (hideTrigger) {
    // Render only the popover content without trigger
    return (
      <div className={cn("w-full", popoverClassName)}>
        {isOpen && children}
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <UIButton
          type="button"
          data-testid={inputDataTestId}
          disabled={disabled}
          variant="outline"
          className={cn(
            'h-10 w-full justify-start text-left font-normal py-3 shadow-none text-base-foreground border-base-input rounded-md',
            'hover:bg-base-accent',
            anchorText === undefined && 'text-base-mutedForeground',
            disabled && 'opacity-50',
            className
          )}
        >
          <CalendarIcon />
          <span
            className={cn(
              'text-base-mutedForeground',
              anchorText && 'text-base-foreground'
            )}
          >
            {anchorText ?? placeholder}
          </span>
        </UIButton>
      </PopoverTrigger>
      <PopoverContent
        data-testid={calendarDataTestId}
        className={cn(
          `w-auto p-${hasActions ? 2 : 0} bg-base-popover min-w-[296px]`,
          popoverClassName
        )}
        align="start"
        side="bottom"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};
